<script lang="ts">
  import Button from './Button.svelte'

  export let cycle = false
  export let hideDelimiters = false
  export let showArrows = true
  export let interval = 6000

  let active = 0
  let items: HTMLDivElement | undefined
  let containerWidth = 0
  let startX = 0
  let transition = 300
  let dragging = false
  let translate = 0
  let observer: ResizeObserver
  let callbacks: WeakMap<Element, (element: Element) => void>
  let intervalRef: ReturnType<typeof setInterval> | undefined
  $: intervalRef = cycle ? setInterval(cycleItems, interval) : undefined

  $: if (!cycle) {
    clearInterval(intervalRef)
  }

  function changeItem() {
    translate = active * -100
  }

  function previousActive() {
    if (items) {
      active = active - 1

      if (active < 0) {
        active = items.childElementCount - 1
      }

      changeItem()
    }
  }

  function nextActive() {
    if (items) {
      active = active + 1

      if (active === items.childElementCount) {
        active = 0
      }

      changeItem()
    }
  }

  function handleDelimiterClick(index: number) {
    if (items) {
      active = index
      changeItem()
    }
  }

  function startDragging(pageX: number) {
    if (items) {
      startX = pageX - items.offsetLeft
      transition = 0
      dragging = true
    }
  }

  function startMouseDragging(event: MouseEvent) {
    startDragging(event.pageX)
  }

  function startTouchDragging(event: TouchEvent) {
    if (event.changedTouches[0]) {
      startDragging(event.changedTouches[0].pageX)
    }
  }

  function stopDragging() {
    if (items) {
      dragging = false
      transition = 300
      const cappedTranslate = Math.round(translate / 100) * 100

      if (cappedTranslate > 0) {
        active = items.childElementCount - 1
      } else if (cappedTranslate < (items.childElementCount - 1) * -100) {
        active = 0
      } else {
        active = cappedTranslate / -100
      }

      changeItem()
    }
  }

  function drag(pageX: number) {
    if (dragging) {
      translate = ((pageX - startX) / containerWidth) * 100 + 100 * active * -1
    }
  }

  function mouseDrag(event: MouseEvent) {
    event.preventDefault()
    drag(event.pageX)
  }

  function touchDrag(event: TouchEvent) {
    if (event.changedTouches[0]) {
      drag(event.changedTouches[0].pageX)
    }
  }

  /*
   * Use resize observer to get client width of items instead of bind:clientWidth due to it inserting an iframe, this messes up the delimiter count
   * When Svelte 5 is released bind:clientWidth will use this method and the iframe will be removed, migrate to bind:clientWidth then
   */
  function resizeObserver(
    element: Element,
    onResize: (element: Element) => void,
  ) {
    if (!observer) {
      callbacks = new WeakMap()
      observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const onResize = callbacks.get(entry.target)
          if (onResize) onResize(entry.target)
        }
      })
    }

    callbacks.set(element, onResize)
    observer.observe(element)

    return {
      destroy: () => {
        callbacks.delete(element)
        observer.unobserve(element)
      },
    }
  }

  function cycleItems() {
    if (items) {
      active = active + 1

      if (active === items.childElementCount) {
        active = 0
      }

      changeItem()
    }
  }
</script>

<svelte:document
  on:mouseup={stopDragging}
  on:touchend={stopDragging}
  on:touchcancel={stopDragging}
  on:mousemove={mouseDrag}
  on:touchmove={touchDrag}
/>
<div
  class="container"
  role="listbox"
  tabindex="0"
  use:resizeObserver={(element) => (containerWidth = element.clientWidth)}
  on:mousedown={startMouseDragging}
  on:touchstart={startTouchDragging}
>
  <div
    class="items"
    bind:this={items}
    style={`transform: translateX(${translate}%); transition: transform ${transition}ms cubic-bezier(0.25, 0.8, 0.5, 1);`}
  >
    <slot />
  </div>
  {#if showArrows}
    <div class="previous">
      <Button
        icon="ph-caret-left"
        type="button"
        variant="text"
        rounded
        on:click={previousActive}
      />
    </div>
    <div class="next">
      <Button
        icon="ph-caret-right"
        type="button"
        variant="text"
        rounded
        on:click={nextActive}
      />
    </div>
  {/if}
  {#if !hideDelimiters && items}
    <div class="delimiters">
      {#each Array.from(items.children || []) as _, index (index)}
        <div class:active={index === active}>
          <Button
            type="button"
            icon="ph-circle-fill"
            variant="text"
            rounded
            on:click={() => handleDelimiterClick(index)}
          />
        </div>
      {/each}
    </div>
  {/if}
</div>

<style lang="scss">
  @use '@bruce/assets/css/breakpoints';

  .container {
    position: relative;
    width: 100%;
    height: 100%;
    overflow-x: hidden;
    cursor: grab;

    .items {
      display: flex;
      width: 100%;
      height: 100%;

      :global(> *) {
        pointer-events: none;
        user-select: none;
        width: 100%;
        height: 100%;
      }
    }

    .previous,
    .next,
    .delimiters {
      --button-icon-color: #fff;
      --color-on-surface-rgb: 255, 255, 255;
      --color-on-surface-hover: #4b4b4b;
      position: absolute;
      display: flex;
    }

    .previous,
    .next {
      align-items: center;
      top: 0;
      width: max-content;
      height: 100%;

      @media screen and (max-width: breakpoints.$breakpoint-sm) {
        display: none;
      }
    }

    .previous {
      left: 0;
      margin-left: 1em;
    }

    .next {
      right: 0;
      margin-right: 1em;
    }

    .delimiters {
      justify-content: center;
      height: max-content;
      align-items: flex-end;
      width: 100%;
      bottom: 1em;
      gap: 0.5em;
      --icon-button-size: 1.5em;

      div {
        width: max-content;

        &:not(.active) {
          --button-icon-color: rgba(255, 255, 255, 0.5);
        }
      }
    }
  }
</style>
